// A shared now query and a local clock; elapsed time never fetches the server.
import { useQuery } from "@tanstack/react-query";
import type {
  TimeBlock,
  TimeBlockerNow,
} from "@trameria/core/timeblocker-shared";
import { m } from "@trameria/i18n";
import { useState } from "react";
import { durationText } from "../agent-chat/names";
import { useApi } from "../app/context";
import { todayIn } from "../cases/dates";
import { apiErrorMessage } from "../data/errors";
import { isOptimisticId } from "../data/optimistic";
import { useTimeZone } from "../me/profile";
import type { ShellBadge } from "../shell/outside/contract";
import { Icon } from "../ui/icon";
import { useElapsedMs, useMinuteClock } from "./clock";
import { blockLabel } from "./day";
import { nowQuery, useStartEndBlock } from "./queries";
import { workingLinks } from "./works";

export function useNowBlock(): TimeBlockerNow {
  const data = useQuery(nowQuery(useApi())).data;
  const now = useMinuteClock();
  const running = data?.running;
  return {
    running:
      running && !running.endedAt && new Date(running.endAt) > now
        ? running
        : null,
    next: data?.next ?? null,
  };
}
/** Something that appears in place (a block just started) fades in. */
const FADE_IN = "animate-[fade-in_150ms_ease-out] motion-reduce:animate-none";

/**
 * Starting and ending a block. One shape in two sizes: a small one on the
 * timeline and in the header (20 px, the elapsed time inside the button that
 * ends it), and the 32 px pill of the detail. A block not started shows a round
 * play button; on the timeline only the block of the present keeps it in
 * sight, the others show it on hover (a touch opens the detail instead). A
 * block whose time is over without being started cannot start and shows
 * nothing.
 */
export function StartEndButton({
  block,
  size = "small",
  onError,
}: {
  block: TimeBlock;
  size?: "small" | "large";
  onError?(e: Error): void;
}) {
  const now = useMinuteClock(),
    timeZone = useTimeZone(),
    mutation = useStartEndBlock();
  const [error, setError] = useState<string | null>(null);
  const large = size === "large";
  const endAt = new Date(block.endAt).getTime();
  const running = !!block.startedAt && !block.endedAt && endAt > now.getTime();
  // The running time counts the seconds, in the agent chat's words.
  const runMs = useElapsedMs(running ? block.startedAt : null);
  if (block.startedAt && !running) {
    const until = Math.min(
      new Date(block.endedAt ?? block.endAt).getTime(),
      endAt,
    );
    // Stopped by mistake: it runs again while its scheduled time is not over.
    const canResume = !!block.endedAt && endAt > now.getTime();
    return (
      <span
        data-no-carry=""
        className={`inline-flex flex-none items-center gap-2 ${large ? "text-[13px]" : "text-[11px]"}`}
      >
        <span
          className={`inline-flex flex-none items-center gap-1 tabular-nums text-muted ${
            large ? "h-8 text-[13px]" : "h-5 text-[11px]"
          }`}
        >
          <Icon kind="check" size={large ? 14 : 12} />
          {large && `${m.timeblocker_block_ended()} · `}
          {durationText(until - new Date(block.startedAt).getTime())}
        </span>
        {canResume && (
          <button
            type="button"
            disabled={isOptimisticId(block.id) || mutation.isPending}
            aria-label={m.timeblocker_resume_block()}
            title={m.timeblocker_resume_block()}
            className={`box-border inline-flex flex-none cursor-pointer items-center whitespace-nowrap border border-accent text-accent hover:border-accent-hover hover:text-accent-hover disabled:cursor-not-allowed disabled:opacity-50 ${
              large
                ? "h-8 gap-[6px] rounded-2xl px-[13px]"
                : "pointer-events-none h-5 w-5 justify-center rounded-[10px] opacity-0 transition-opacity duration-150 focus-visible:pointer-events-auto focus-visible:opacity-100 pointer-fine:group-hover/block:pointer-events-auto pointer-fine:group-hover/block:opacity-100"
            }`}
            onClick={() =>
              mutation.mutate(
                {
                  day: todayIn(timeZone),
                  timeZone,
                  block,
                  end: false,
                  resume: true,
                },
                {
                  onError: (e) =>
                    onError
                      ? onError(e)
                      : setError(
                          apiErrorMessage(e, m.timeblocker_save_failed()),
                        ),
                },
              )
            }
          >
            <Icon kind="play" size={large ? 12 : 10} />
            {large && m.timeblocker_resume_block()}
          </button>
        )}
        {error && (
          <span role="alert" className="text-danger">
            {error}
          </span>
        )}
      </span>
    );
  }
  if (endAt <= now.getTime()) return null;
  const present = new Date(block.startAt).getTime() <= now.getTime();
  const label = running
    ? m.timeblocker_end_block()
    : m.timeblocker_start_block();
  const elapsed = running && durationText(runMs);
  const shape = running
    ? large
      ? `h-8 gap-2 rounded-2xl px-[13px] text-[13px] ${FADE_IN}`
      : `h-5 gap-[5px] rounded-[10px] pl-[7px] pr-[6px] text-[11px] ${FADE_IN}`
    : large
      ? "h-8 gap-[6px] rounded-2xl border-accent bg-accent pl-[11px] pr-[14px] text-[13px] text-accent-fg hover:border-accent-hover hover:bg-accent-hover"
      : `h-5 w-5 justify-center rounded-[10px] border-accent bg-accent text-accent-fg hover:border-accent-hover hover:bg-accent-hover ${
          present
            ? ""
            : "pointer-events-none opacity-0 transition-opacity duration-150 focus-visible:pointer-events-auto focus-visible:opacity-100 pointer-fine:group-hover/block:pointer-events-auto pointer-fine:group-hover/block:opacity-100"
        }`;
  return (
    <span
      data-no-carry=""
      className={`inline-flex flex-none items-center gap-2 ${
        large ? "text-[13px]" : "text-[11px]"
      }`}
    >
      <button
        type="button"
        disabled={isOptimisticId(block.id) || mutation.isPending}
        aria-label={label}
        className={`box-border inline-flex flex-none cursor-pointer items-center whitespace-nowrap border disabled:cursor-not-allowed disabled:opacity-50 ${
          running
            ? "border-danger/35 bg-surface text-danger tabular-nums hover:border-danger"
            : ""
        } ${shape}`}
        onClick={() =>
          mutation.mutate(
            { day: todayIn(timeZone), timeZone, block, end: running },
            {
              onError: (e) =>
                onError
                  ? onError(e)
                  : setError(apiErrorMessage(e, m.timeblocker_save_failed())),
            },
          )
        }
      >
        {large ? (
          <>
            <Icon kind={running ? "stop" : "play"} size={12} />
            {label}
            {running && <span className="opacity-80">{elapsed}</span>}
          </>
        ) : running ? (
          <>
            {elapsed}
            <Icon kind="stop" size={9} />
          </>
        ) : (
          <Icon kind="play" size={10} />
        )}
      </button>
      {error && (
        <span role="alert" className="text-danger">
          {error}
        </span>
      )}
    </span>
  );
}
export function RunningBlockLine() {
  const { running } = useNowBlock();
  const working = running ? workingLinks(running, Date.now()) : [];
  return running ? (
    <span
      className={`inline-flex h-[26px] max-w-[18em] items-center gap-2 rounded-[13px] bg-hover pl-3 pr-[3px] text-[12px] text-body ${FADE_IN}`}
    >
      {/* What is worked on now, else the Block's own name. */}
      <span
        className={`flex min-w-0 items-center gap-1 ${working.length ? "text-accent-deep" : ""}`}
      >
        <span className="min-w-0 truncate">
          {working.length
            ? (working[0].stepTitle ?? working[0].caseTitle)
            : blockLabel(running)}
        </span>
        {working.length > 1 && (
          <span className="flex-none">+{working.length - 1}</span>
        )}
      </span>
      <StartEndButton block={running} />
    </span>
  ) : null;
}
export function useRunningBadgeData(): ShellBadge | null {
  const { running } = useNowBlock();
  return running
    ? { kind: "dot", tone: "danger", label: blockLabel(running) }
    : null;
}
export function useRunningBadge() {
  const badge = useRunningBadgeData();
  return badge ? (
    <span
      role="img"
      aria-label={badge.label}
      className="inline-block h-[6px] w-[6px] rounded-full bg-danger"
    />
  ) : null;
}
